// src/Component/Services.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { 
  Zap, ShieldCheck, AreaChart, Layers3, BrainCircuit, Code, 
  CheckCircle, BarChart3, TrendingUp, ArrowRight, UserPlus, Settings, PlayCircle 
} from 'lucide-react';

const servicesData = [
  {
    icon: BrainCircuit,
    title: 'Multi-Strategy Algo Bridge',
    description: 'Ek hi dashboard se multiple broker accounts aur alag-alag strategies ko connect aur manage karein bina kisi jhanjhat ke.',
  },
  {
    icon: Zap,
    title: 'Ultra-Low Latency Execution',
    description: 'Milliseconds mein trade execute karein. Humara optimized engine slippage ko minimize karta hai aur best price catch karta hai.',
  },
  {
    icon: ShieldCheck,
    title: 'Dynamic Risk Management',
    description: 'Automated stop-loss, trailing profits, aur capital protection. Aapka paisa hamesha safe parameters ke andar trade karega.',
  },
  {
    icon: AreaChart,
    title: 'Performance Analytics',
    description: 'Apni strategy ki performance ko clear graphs aur data points ke through visualize karein. Win rate aur drawdowns check karein.',
  },
  {
    icon: Layers3,
    title: 'Strategy Marketplace',
    description: 'Top quantitative analysts dwara banai gayi proven strategies ko browse karein, backtest karein, aur subscribe karein.',
  },
  {
    icon: Code,
    title: 'Custom Algo Development',
    description: 'Agar aapke paas apni koi unique trading strategy hai, toh humari expert team usko fully automated code mein convert karegi.',
  },
];

const Services: React.FC = () => {
  return (
    <div className="bg-slate-50 text-slate-900 font-[Urbanist] min-h-screen overflow-hidden">
      
      {/* =========================================
          1. HERO SECTION (Transparent & Floating Layout)
          ========================================= */}
      <section className="relative pt-32 pb-20 px-6 max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-12 min-h-[80vh]">
        
        {/* Floating Background Elements (Transparent Look) */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden z-0">
           {/* Soft Glows */}
           <div className="absolute top-10 left-[-10%] w-96 h-96 bg-red-400/10 rounded-full blur-[100px]"></div>
           <div className="absolute bottom-10 right-[-10%] w-[500px] h-[500px] bg-blue-500/10 rounded-full blur-[120px]"></div>
           
           {/* Floating Mini Card 1 */}
           <motion.div 
             animate={{ y: [0, -20, 0], rotate: [0, 5, 0] }}
             transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
             className="hidden lg:flex absolute top-[15%] right-[45%] bg-white/60 backdrop-blur-md p-4 rounded-2xl shadow-xl border border-white/40 items-center gap-3"
           >
             <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center"><TrendingUp className="text-green-600" size={20}/></div>
             <div>
               <p className="text-xs text-slate-500 font-bold">Nifty 50</p>
               <p className="text-sm font-black text-slate-800">+1.24%</p>
             </div>
           </motion.div>

           {/* Floating Mini Card 2 */}
           <motion.div 
             animate={{ y: [0, 20, 0], rotate: [0, -5, 0] }}
             transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 1 }}
             className="hidden lg:flex absolute bottom-[20%] left-[45%] bg-white/60 backdrop-blur-md p-4 rounded-2xl shadow-xl border border-white/40 items-center gap-3 z-20"
           >
             <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center"><Zap className="text-blue-600" size={20}/></div>
             <div>
               <p className="text-xs text-slate-500 font-bold">Execution</p>
               <p className="text-sm font-black text-slate-800">0.01 ms</p>
             </div>
           </motion.div>
        </div>

        {/* Left Content */}
        <motion.div 
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7 }}
          className="w-full lg:w-[50%] flex flex-col items-start text-left relative z-10"
        >
          <span className="text-blue-900 font-bold tracking-wider uppercase text-sm mb-4 flex items-center gap-2 bg-blue-100/50 px-4 py-2 rounded-full border border-blue-200/50">
            <BarChart3 size={16} /> Premium Ecosystem
          </span>
          <h1 className="text-5xl md:text-6xl lg:text-[4rem] font-black mb-6 tracking-tight leading-[1.1] text-slate-900">
            Advanced Trading <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-orange-800">Services & Solutions.</span>
          </h1>
          <p className="text-lg md:text-xl text-slate-600 mb-8 leading-relaxed max-w-lg">
            Humari services specially design ki gayi hain taaki aap manual trading ki limitations ko chhod kar, full data-driven automated systems ka fayda utha sakein.
          </p>
          <div className="flex items-center gap-4">
            <button className="bg-red-700 hover:bg-red-600 text-white px-8 py-4 rounded-xl font-bold text-lg shadow-[0_10px_30px_rgba(220,38,38,0.3)] transition-all hover:-translate-y-1 flex items-center gap-2">
              Explore Services <ArrowRight size={20} />
            </button>
            <button className="px-8 py-4 rounded-xl font-bold text-lg text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 hover:shadow-sm transition-all flex items-center gap-2">
              <PlayCircle size={20} className="text-blue-600"/> Watch Demo
            </button>
          </div>
        </motion.div>

        {/* Right Image (Static Clean Image without motion) */}
        <div className="w-full lg:w-[50%] relative z-10">
          <img 
            src="https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?auto=format&fit=crop&w=800&q=80" 
            alt="Trading Graph Details" 
            className="w-full h-auto rounded-[2.5rem] shadow-2xl"
          />
        </div>
      </section>

      {/* =========================================
          2. OUR SERVICES GRID
          ========================================= */}
      <section className="py-20 bg-white relative border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-black text-slate-900 mb-4">Humari Core Services</h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Retail traders se lekar institutional investors tak, humara platform sabhi ke liye high-end automated solutions provide karta hai.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {servicesData.map((service, index) => (
              <motion.div 
                key={index}
                whileHover={{ y: -8 }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-slate-50/50 border border-slate-200 p-8 rounded-[2rem] shadow-sm hover:shadow-xl hover:shadow-blue-900/5 hover:border-blue-200 transition-all group"
              >
                <div className="w-14 h-14 rounded-2xl bg-white flex items-center justify-center border border-slate-200 shadow-sm mb-6 group-hover:scale-110 group-hover:border-red-200 group-hover:bg-red-50 transition-all">
                  <service.icon className="text-blue-900 group-hover:text-red-600 transition-colors" size={28} />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-3 group-hover:text-blue-900 transition-colors">
                  {service.title}
                </h3>
                <p className="text-slate-600 font-medium leading-relaxed">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================
          3. GRAPH & DEEP DIVE SECTION (With Pure CSS Chart Fix)
          ========================================= */}
      <section className="py-24 max-w-7xl mx-auto px-6">
        <div className="bg-slate-900 rounded-[2.5rem] overflow-hidden shadow-[0_20px_50px_rgba(15,23,42,0.3)] relative">
          <div className="absolute inset-0 bg-[radial-gradient(#334155_1px,transparent_1px)] [background-size:20px_20px] opacity-20"></div>
          
          <div className="grid lg:grid-cols-2 items-center relative z-10">
            {/* Left Side: Detail & Points */}
            <div className="p-10 md:p-16">
              <span className="text-red-400 font-bold tracking-widest uppercase text-sm mb-4 flex items-center gap-2">
                <BarChart3 size={18}/> Data-Driven Precision
              </span>
              <h2 className="text-3xl md:text-4xl font-black text-white mb-6 leading-tight">
                Graph based Analysis & <br /> Real-Time Tracking
              </h2>
              <p className="text-slate-300 text-lg leading-relaxed mb-8">
                Humari services sirf code tak seemit nahi hain. Aapko ek powerful dashboard milta hai jahan aap market trends, strategy success rate, aur live P&L data detailed graphs ke through samajh sakte hain.
              </p>
              
              <ul className="space-y-5">
                {[
                  "Live Candlestick & Tick-by-Tick Data",
                  "Historical Backtesting Graphs",
                  "Risk vs Reward Ratio Visualizer",
                  "Heatmaps & Volume Indicators"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-4 text-slate-200 font-medium text-lg">
                    <CheckCircle className="text-red-500 shrink-0" size={24} /> {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right Side: PURE CSS DASHBOARD MOCKUP (Never Breaks) */}
            <div className="p-8 lg:p-12 h-full flex flex-col justify-center">
              <div className="w-full bg-slate-800/80 backdrop-blur-md rounded-2xl border border-slate-700 shadow-2xl p-6 relative overflow-hidden">
                 {/* Top Stats */}
                 <div className="flex justify-between items-center mb-8 border-b border-slate-700 pb-4">
                    <div>
                      <p className="text-slate-400 text-xs font-bold uppercase tracking-wider mb-1">Total P&L</p>
                      <p className="text-green-400 text-2xl font-black">+ ₹45,230.00</p>
                    </div>
                    <div className="px-3 py-1 bg-green-400/10 border border-green-400/20 text-green-400 rounded-md text-sm font-bold flex items-center gap-1">
                      <TrendingUp size={16}/> Live
                    </div>
                 </div>
                 
                 {/* Fake CSS Chart Bars */}
                 <div className="flex items-end justify-between gap-2 h-48 relative">
                    {/* Glowing Line behind bars */}
                    <svg className="absolute inset-0 w-full h-full opacity-50" preserveAspectRatio="none">
                      <path d="M0,150 Q50,50 100,120 T200,80 T300,100 T400,20" fill="none" stroke="#f87171" strokeWidth="3" vectorEffect="non-scaling-stroke"/>
                    </svg>

                    {[40, 70, 45, 90, 60, 110, 80, 130].map((height, i) => (
                      <motion.div 
                        key={i}
                        initial={{ height: 0 }}
                        whileInView={{ height: `${height}%` }}
                        transition={{ duration: 1, delay: i * 0.1 }}
                        className={`w-full rounded-t-sm ${i % 2 === 0 ? 'bg-red-500/80 hover:bg-red-400' : 'bg-green-500/80 hover:bg-green-400'} transition-colors cursor-pointer relative z-10`}
                      />
                    ))}
                 </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================
          4. NEW EXTRA SECTION: HOW IT WORKS
          ========================================= */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <span className="text-blue-600 font-bold tracking-widest uppercase text-sm mb-3 block">Simple Process</span>
          <h2 className="text-3xl md:text-5xl font-black text-slate-900 mb-16">Start Algo Trading in 3 Steps</h2>
          
          <div className="grid md:grid-cols-3 gap-10 relative">
            {/* Connecting Line for Desktop */}
            <div className="hidden md:block absolute top-1/4 left-[15%] right-[15%] h-0.5 bg-gradient-to-r from-red-200 via-blue-200 to-green-200 z-0"></div>

            {[
               { icon: UserPlus, title: "1. Connect Account", desc: "Apne existing demat account ko humari API ke through securely link karein.", color: "text-red-600", bg: "bg-red-100" },
               { icon: Settings, title: "2. Choose Strategy", desc: "Marketplace se proven strategy select karein ya apni custom strategy banwayein.", color: "text-blue-600", bg: "bg-blue-100" },
               { icon: TrendingUp, title: "3. Automate & Earn", desc: "System ko start karein aur trades ko automatically execute hote hue dekhein.", color: "text-green-600", bg: "bg-green-100" }
            ].map((step, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.2 }}
                className="relative z-10 flex flex-col items-center bg-white p-6 rounded-3xl shadow-[0_10px_40px_rgba(0,0,0,0.04)] border border-slate-100"
              >
                <div className={`w-20 h-20 rounded-full ${step.bg} flex items-center justify-center mb-6 shadow-inner border-4 border-white ring-1 ring-slate-100`}>
                  <step.icon className={step.color} size={32} />
                </div>
                <h3 className="text-2xl font-black text-slate-900 mb-3">{step.title}</h3>
                <p className="text-slate-600 font-medium">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================
          5. BOTTOM CTA BANNER
          ========================================= */}
      <section className="bg-gradient-to-r from-blue-950 via-blue-900 to-indigo-800 py-16 relative overflow-hidden mt-10">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
        <div className="max-w-4xl mx-auto px-6 relative z-10 text-center flex flex-col items-center">
          <div className="w-20 h-20 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center mb-6 border border-white/20">
            <Zap className="text-white" size={40} fill="currentColor" />
          </div>
          <h2 className="text-3xl md:text-5xl font-black text-white mb-6">
            Ready to Upgrade Your Trading?
          </h2>
          <p className="text-blue-100 text-xl font-medium mb-10">
            Judein un hazaron traders ke sath jo apni trading ko fully automate karke market mein edge le rahe hain.
          </p>
          <button className="bg-white text-blue-900 px-10 py-4 rounded-xl font-black text-lg shadow-[0_0_40px_rgba(255,255,255,0.3)] transition-all hover:bg-slate-100 hover:scale-105 uppercase tracking-wider flex items-center gap-2">
            Open Free Account Now <ArrowRight size={20}/>
          </button>
        </div>
      </section>

    </div>
  );
};

export default Services;